import React, { createRef } from "react";
// import { DEFAULT_UNWEIGHTED_NOTES } from "../../functions/backup_jsaudioapi/convertHistogramToNotes";
import { AppContextType } from "../../../AppContext";
import { soundSingleChord } from "../../../functions/playNotesAudio";
import { ChordHistoryInteractMode } from "../../../models/ChordHistoryOptions.model";
import { ChordInScaleEdit } from "../../../models/wasm.model";
import { AudioHistoryState } from "../../../statemachines/AudioHistoryState";
import InfoCard from "../../layout_reuse/InfoCard";
import MusicScaleName from "../MusicScaleName";
import "./HistoryMonitor.css";
import HistoryMonitorColumn, { ChordPopupArgs } from "./HistoryMonitorColumn";
import HistoryMonitorHeader from "./HistoryMonitorHeader";
import { MusicScale } from "../../../../wasm/pkg/wasm";

interface Props {
  audioHistory: AudioHistoryState;
  /** disable some features (e.g. replay) while capture is active */
  isCaptureActive: boolean;
  onDeleteChord: (scaleId: string, tickTimeIndex: number, chordIndex: number) => void;
  onDeleteScale: (scaleId: string) => void;
  openChordPopup: (args: ChordPopupArgs) => void;
  onChooseChordForInstrumentation: (chord: ChordInScaleEdit, scale: MusicScale) => void;
}
interface State {
  isReplaying: boolean;
  replayIntervalHandle?: any;
  replayTickIndex: number;
  replayScaleId?: string;
  replayChordIndex: number;
}

export default class HistoryMonitor extends React.Component<Props, State> {
  declare context: React.ContextType<typeof AppContextType>;
  constructor(props: Props) {
    super(props);
    this.state = {
      isReplaying: false,
      replayIntervalHandle: undefined,
      replayTickIndex: 0,
      replayScaleId: undefined,
      replayChordIndex: 0,
    };
  }
  scrollRef = createRef<HTMLDivElement>();
  // views, prioritized:
  // 1. the currently detected notes, with the quiet notes under them, showing how individual detected notes in multiple become chords across multiple time

  // General problem: simplest possible view of chord data over time
  //   Complications:
  //      uncertainty and the need to expose guesses
  //      applying hindsight to adjust the history
  //      identifying patterns
  //      consolidating patterns

  // problems
  /**
     * Save every scale that happened.  If the scale disappears permanantly, maybe just collapse the old info... when key changes, want it agin
     * Notes and chords stream out to the right of a given scale.
     * So your first div is perhaps separate non-scrolling left-pinned headers:  each scale, with height matching the scroll content.
     * ...and subsequent divs are chord / note information.

    */
  componentDidUpdate(prevProps: Props, prevState: State) {
    /*
     * Automatic Scrolling.
     */
    if (this.context.chordHistoryOptions.autoScroll) {
      if (this.scrollRef.current) {
        let timestamp = undefined;
        if (prevState.replayTickIndex !== this.state.replayTickIndex && this.state.isReplaying) {
          timestamp = this.props.audioHistory.timestamps[this.state.replayTickIndex];
        } else if (prevProps.audioHistory.newestTimestampIndex !== this.props.audioHistory.newestTimestampIndex) {
          timestamp = this.props.audioHistory.newestTimestamp;
        }
        if (timestamp) {
          // Caution: reaching into HistoryMonitorColumn internals
          const latestTsElement = document.getElementById(`tick-${timestamp / 1000}`);
          const left = (latestTsElement?.offsetLeft || 0) - this.scrollRef.current.clientWidth;
          this.scrollRef.current.scrollTo({ left, behavior: "smooth" });
        }
      }
    }
    /*
     * Clean up the replay interval if new audio capture begins/ends.
     */
    if (prevProps.isCaptureActive !== this.props.isCaptureActive) {
      if (this.state.isReplaying && this.state.replayIntervalHandle) {
        clearInterval(this.state.replayIntervalHandle);
        this.setState({
          isReplaying: false,
          replayIntervalHandle: undefined,
          replayTickIndex: 0,
        });
      } else {
        this.setState({
          replayTickIndex: 0,
        });
      }
    }
  }

  curryHandlePinnedScaleClick = (scaleId: string) => () => {
    if (this.context.chordHistoryOptions.interactMode === ChordHistoryInteractMode.DELETE) {
      this.props.onDeleteScale(scaleId);
    }
  };

  renderLeftPinnedColumn(audioHistory: AudioHistoryState) {
    const scaleIdList = Object.keys(audioHistory.chordsInScales).toSorted();
    const visibleScales = scaleIdList.map((sid) => ({ sid, chordsInScale: audioHistory.chordsInScales[sid] }));
    const hideNotes = this.context.chordHistoryOptions.noteRow.hideNotes;
    return (
      <div className="audio-history-pinned-column">
        <div className="timer-tick-axis-count"></div>
        {!hideNotes && <div className="history-notes-pinned-label">Notes</div>}
        <div className="history-chords-pinned-label-wrapper">
          {visibleScales.length < 2 ? (
            <div className="history-chords-pinned-label">Chords</div>
          ) : (
            visibleScales.map(({ sid, chordsInScale }, index) => {
              const rowNum = index + 1;
              const rowClasses = `history-scales-row${rowNum > 9 ? "" : 0}${rowNum} history-chords-pinned-label`;
              return (
                <div key={sid} className={rowClasses} onClick={this.curryHandlePinnedScaleClick(sid)}>
                  <MusicScaleName scale={chordsInScale.scale} />
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  }

  uploadPriorHistory = () => {
    console.log("TODO uploadPriorHistory");
  };

  downloadHistory = () => {
    console.log("TODO downloadHistory");
  };

  beginReplayChords = () => {
    if (!this.state.replayIntervalHandle) {
      const replayIntervalHandle = setInterval(async () => {
        const { isReplaying, replayTickIndex: lastReplayTickIndex } = this.state;
        const { audioHistory } = this.props;
        if (isReplaying) {
          const replayTickIndex = lastReplayTickIndex + 1;
          if (replayTickIndex < audioHistory.entryCounter) {
            // FIXME: this isn't great.  It's "what is the best scale, and the best chord"
            const chordPerScale: { [scaleId: string]: { scaleId: string; chord: ChordInScaleEdit | undefined } } = {};
            Object.keys(this.props.audioHistory.chordsInScales).forEach((scaleId) => {
              chordPerScale[scaleId] = { scaleId, chord: this.props.audioHistory.chordsInScales[scaleId].chordHistory[replayTickIndex][0] };
            });
            const psid = this.props.audioHistory.primaryScaleId!;
            const replayChordIndex = 0; // ignore extra chords, user can delete the bad ones
            const { chord, scaleId: replayScaleId } = chordPerScale[psid]?.chord
              ? chordPerScale[psid]
              : Object.values(chordPerScale).find((sc) => sc.chord) || { chord: undefined, scaleId: undefined };
            if (chord?.notes) {
              const arpeggiate = this.context.chordHistoryOptions.sounding.arpeggiation;
              const bassNoteId = chord.bass_note_id;
              soundSingleChord(chord.notes, { arpeggiate, bassNoteId });
            }
            this.setState({
              replayTickIndex,
              replayScaleId,
              replayChordIndex,
            });
          } else {
            this.pauseReplayingChords();
          }
        } else {
          console.warn("replay interval was not properly stopped.");
        }
      }, 500);

      this.setState({
        isReplaying: true,
        replayIntervalHandle,
      });
    }
  };

  pauseReplayingChords = () => {
    if (this.state.replayIntervalHandle) {
      clearInterval(this.state.replayIntervalHandle);
      const replayTickIndex = this.state.replayTickIndex + 1 >= this.props.audioHistory.entryCounter ? 0 : this.state.replayTickIndex;
      // console.log("resetting tick index to ", replayTickIndex);
      this.setState({
        isReplaying: false, // FIXME: Need a lingering state to keep the paused line highlighted
        replayIntervalHandle: undefined,
        replayTickIndex,
      });
    }
  };

  handleTimerAxisNavigation = (timestamp: number) => {
    let replayTickIndex = this.props.audioHistory.timestamps.findIndex((t) => t === timestamp);
    replayTickIndex = Math.max(0, replayTickIndex - 1);
    if (replayTickIndex || replayTickIndex === 0) {
      this.setState({ replayTickIndex });
    }
  };

  buildInteractModeClasses = () => {
    const { interactMode } = this.context.chordHistoryOptions;
    if (interactMode === ChordHistoryInteractMode.DELETE) {
      return "chord-mode-delete";
    } else if (interactMode === ChordHistoryInteractMode.EDIT) {
      return "chord-mode-edit";
    }
    return "chord-mode-sounding";
  };

  buildCaptureModeClasses = () => {
    return this.props.isCaptureActive ? "capture-active" : "capture-inactive";
  };

  buildHistoryRowClasses = () => {
    return Object.keys(this.props.audioHistory.chordsInScales)
      .toSorted()
      .map((scaleId, index) => {
        const rowNum = index + 1;
        const numEntries = Math.min(7, this.props.audioHistory.chordsInScales[scaleId].highestNumOfChords);
        return `history-row${rowNum > 9 ? "" : 0}${rowNum}-has-height-${numEntries}`;
      })
      .join(" ");
  };

  render() {
    const { audioHistory, isCaptureActive } = this.props;
    const { isReplaying, replayTickIndex } = this.state;
    /** While processing, the newest column is NOT highlighted if it's at the end. To provide contrast vs. re-do */
    const isRightmostColumnNewest = audioHistory.newestTimestamp === audioHistory.timestamps[audioHistory.timestamps.length - 1];
    const chordModeClass = this.buildInteractModeClasses();
    const captureModeClass = this.buildCaptureModeClasses();
    const historyRowClasses = this.buildHistoryRowClasses();
    return (
      <InfoCard includesHeaderBar className={`area-history-scrolling ${chordModeClass} ${captureModeClass} ${historyRowClasses}`}>
        <div className="info-card-header-bar">
          <HistoryMonitorHeader
            isReplaying={isReplaying}
            isHistoryExist={!audioHistory.isPlaceholder}
            isCaptureActive={isCaptureActive}
            onBeginReplay={this.beginReplayChords}
            onPauseReplay={this.pauseReplayingChords}
            onDownloadHistory={this.downloadHistory}
            onUploadHistory={this.uploadPriorHistory}
          />
        </div>
        <div className="audio-history-container">
          {this.renderLeftPinnedColumn(audioHistory)}
          <div className="audio-history-scroll-container" ref={this.scrollRef}>
            {audioHistory.noteHistory.map((_note, index) => {
              const key = `${index}-scroll-second`;
              return (
                <div key={key} className="audio-history-scroll-container-inner">
                  <HistoryMonitorColumn
                    audioHistory={audioHistory}
                    isCaptureActive={isCaptureActive}
                    timeIndex={index}
                    highlightColumn={
                      isReplaying
                        ? index === replayTickIndex
                        : !isRightmostColumnNewest && audioHistory.newestTimestamp === audioHistory.timestamps[index]
                    }
                    onDeleteChord={this.props.onDeleteChord}
                    onDeleteScale={this.props.onDeleteScale}
                    openChordPopup={this.props.openChordPopup}
                    onChooseChordForInstrumentation={this.props.onChooseChordForInstrumentation}
                    onTimerAxisNavigation={this.handleTimerAxisNavigation}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </InfoCard>
    );
  }
}

HistoryMonitor.contextType = AppContextType;
